import React, { useState } from "react";
import { Sequence, AbsoluteFill } from "remotion";
import type { TrackType } from "../remotion-timeline/types";
import { Item } from "./item";
import Draggable from "react-draggable";
import { useTimelineContext } from "../remotion-timeline/context/use-timeline-context";
import Selectable from "./selectable";
import { Resizable } from "re-resizable";
import { PlayerRef } from "@remotion/player";
import { useSettings } from "../context";

export const Layer: React.FC<{
  track: TrackType;
  playerRef: React.RefObject<PlayerRef | null>;
}> = ({ track, playerRef }) => {
  const { changeItem, setSelectedItem, selectedItem } = useTimelineContext();
  const { updateSizeSettings, updatePositionSettings } = useSettings();
  const [isResizing, setIsResizing] = useState(false);

  const onResizeStart = () => {
    setIsResizing(true);
    document.body.style.pointerEvents = "none";
  };

  const onResize = (e: any, direction: any, ref: any, d: any) => {
    if (selectedItem) {
      const width = parseInt(ref.style.width, 10);
      const height = parseInt(ref.style.height, 10);

      updateSizeSettings({ width, height });
    }
  };

  const onResizeStop = (e: any, direction: any, ref: any, d: any) => {
    setIsResizing(false);
    document.body.style.pointerEvents = "";
  };

  return (
    <>
      {track.items.map((item) => {
        if (item.type === "video" || item.type === "audio") {
          return (
            <Sequence
              key={item.id}
              from={item.from}
              durationInFrames={item.durationInFrames}
            >
              <AbsoluteFill>
                <Item item={item} />
              </AbsoluteFill>
            </Sequence>
          );
        }

        const isSelected = selectedItem === item.id;

        const handleDragStart = () => {
          if (isResizing) return;
          setSelectedItem(item.id);
          changeItem(item.id, (currentItem) => ({
            ...currentItem,
            isDragging: true,
          }));
        };

        const handleDrag = (_e: any) => {
          if (isResizing) return;
          setSelectedItem(item.id);
        };

        const handleDragStop = (_e: any, data: { x: number; y: number }) => {
          if (isResizing) return;
          updatePositionSettings({ x: data.x, y: data.y });
          changeItem(item.id, (currentItem) => ({
            ...currentItem,
            isDragging: false,
          }));
        };

        if (item.type === "text" || item.type === "image") {
          return (
            <Sequence
              key={item.id}
              from={item.from}
              durationInFrames={item.durationInFrames}
            >
              <AbsoluteFill>
                {playerRef.current?.isFullscreen() ? (
                  <Draggable
                    position={{ x: item.position.x, y: item.position.y }}
                    disabled
                  >
                    <div>
                      <Item item={item} />
                    </div>
                  </Draggable>
                ) : (
                  <Draggable
                    position={{ x: item.position.x, y: item.position.y }}
                    onStart={handleDragStart}
                    onDrag={handleDrag}
                    onStop={handleDragStop}
                    disabled={isResizing}
                    handle={
                      isResizing ? ".no-drag-handle-during-resize" : undefined
                    }
                  >
                    <div>
                      <Selectable id={item.id} selected={isSelected}>
                        <Resizable
                          onResizeStart={onResizeStart}
                          onResizeStop={onResizeStop}
                          onResize={onResize}
                          defaultSize={{
                            width: item.size.width,
                            height: item.size.height,
                          }}
                        >
                          <div>
                            <Item item={item} />
                          </div>
                        </Resizable>
                      </Selectable>
                    </div>
                  </Draggable>
                )}
              </AbsoluteFill>
            </Sequence>
          );
        }
        return null;
      })}
    </>
  );
};
